import type { Transaction } from "@rocicorp/zero";
import { type Context, userFacingError } from "../../authorization";
import { type Schema, zql } from "../../generatedDrizzleSchema";
import { hasAccess } from "../access";
import { type Permission } from "../permissions";
import { BUILTIN_ROLES, type BuiltinRoleId } from "../roles";
import { applyGrantDiff, diffGrants } from "./grantContent";
import { assertBuiltinRoleScope, assertNoAllowAll } from "./grantShape";

type Principal = { userGroupId: string } | { serviceAccountId: string };

export async function setTenantRoles(
  tx: Transaction<Schema>,
  principal: Principal,
  builtinRoleIds: readonly string[],
  customRoleIds: readonly string[],
  tenantId: string,
  granter: Context
): Promise<void> {
  const conferred = new Set<Permission>();

  for (const id of builtinRoleIds) {
    assertBuiltinRoleScope(id, "tenant");
    const permissions = BUILTIN_ROLES[id as BuiltinRoleId]!.permissions;
    assertNoAllowAll(permissions);
    for (const p of permissions) conferred.add(p);
  }

  if (customRoleIds.length > 0) {
    const customRoles = await tx.run(
      zql.roles
        .where("tenantId", tenantId)
        .where(({ cmp, or }) => or(...customRoleIds.map((id) => cmp("id", id))))
        .related("rolePermissions")
    );
    for (const id of customRoleIds) {
      const role = customRoles.find((r) => r.id === id);
      if (!role || role.scopeKind !== "tenant") {
        throw userFacingError(`Invalid tenant custom role: ${id}`);
      }
      assertNoAllowAll(role.rolePermissions);
      for (const rp of role.rolePermissions ?? []) {
        conferred.add(rp.permission as Permission);
      }
    }
  }

  // No-escalation guard, mirroring the service-account grant path: the granter
  // may only confer a tenant permission they already hold, since a group member
  // inherits the group's grants (tenant admins pass via allow_all). Checks the
  // acting principal (`granter.principal`, e.g. a service account) — without it
  // a service account granter is validated as a bogus user (its userId is its
  // own SA id) and holds nothing, so it could never confer roles it actually
  // has.
  for (const permission of conferred) {
    const granterHas = await hasAccess({
      tx,
      userId: granter.userId,
      principal: granter.principal,
      scopeId: tenantId,
      tenantId,
      permission,
    });
    if (!granterHas) {
      throw userFacingError(
        `You cannot grant "${permission}" because you do not hold it`
      );
    }
  }

  const existing =
    "userGroupId" in principal
      ? await tx.run(
          zql.roleGrants
            .where("userGroupId", principal.userGroupId)
            .where("scopeId", tenantId)
        )
      : await tx.run(
          zql.roleGrants
            .where("serviceAccountId", principal.serviceAccountId)
            .where("scopeId", tenantId)
        );

  const desired = [
    ...builtinRoleIds.map((builtinRoleId) => ({ ...principal, builtinRoleId })),
    ...customRoleIds.map((customRoleId) => ({ ...principal, customRoleId })),
  ];

  const diff = diffGrants(existing, desired);
  await applyGrantDiff(tx, tenantId, diff, tenantId);
}
